import { useEffect, useRef, useState } from "react";
import type { User as AuthUser } from "firebase/auth";
import { clearDraft, isEmptyDraft, readDraft, writeDraft } from "../draft";
import type { DraftItems } from "./useDraftItems";
import type { OrderImages } from "./useOrderImages";
import type { StoneHandlingDraft } from "./useStoneHandling";

export function useDraftPersistence({
  user,
  draftItems,
  images,
  stones,
  activeStep,
  stepInUrl,
  goToStep,
}: {
  user: AuthUser | null;
  draftItems: DraftItems;
  images: OrderImages;
  stones: StoneHandlingDraft;
  activeStep: number;
  stepInUrl: boolean;
  goToStep: (step: number) => void;
}) {
  const loaded = useRef(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!user || loaded.current) return;
    loaded.current = true;
    const draft = readDraft(user.uid);
    if (draft) {
      draftItems.restore(draft.items);
      images.restore(draft.orderImagePaths);
      stones.restore(draft.stoneMode, draft.selectedStoneItemIndexes);
      if (!stepInUrl) goToStep(draft.activeStep);
    }
    setReady(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (!user || !ready) return;
    const draft = {
      items: draftItems.items,
      stoneMode: stones.mode,
      selectedStoneItemIndexes: stones.selectedItemIndexes,
      orderImagePaths: images.paths,
      activeStep,
    };
    if (isEmptyDraft(draft)) clearDraft(user.uid);
    else writeDraft(user.uid, draft);
  }, [
    user,
    ready,
    draftItems.items,
    stones.mode,
    stones.selectedItemIndexes,
    images.paths,
    activeStep,
  ]);

  return {
    clear: () => {
      if (user) clearDraft(user.uid);
    },
  };
}
